import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, RotateCcw, AlertCircle } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { formatCents, todayISO } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReturnableLine {
  estimate_item_id: number;
  item_id: number | null;
  description: string;
  category: string | null;
  sold_qty: number;
  already_returned_qty: number;
  returnable_qty: number;
  effective_unit_price: number;
}

interface ReturnableResponse {
  data: {
    estimate: {
      id: number;
      number: string;
      customer_id: number;
      customer_name: string;
      total: number;
    };
    lines: ReturnableLine[];
  };
}

interface CreateReturnResponse {
  data: {
    return: { id: number; number: string };
  };
}

interface EditReturnResponse {
  data: {
    return: {
      id: number;
      number: string;
      estimate_id: number;
      status: string;
      date: string;
      notes: string | null;
    };
    items: Array<{ estimate_item_id: number | null; qty: number }>;
  };
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function CreateReturnPage({ mode = 'create' }: { mode?: 'create' | 'edit' }) {
  const { id } = useParams<{ id: string }>();
  const isEdit = mode === 'edit';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  // ── Return form state ──
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  // Map of estimate_item_id → qty string
  const [qtyMap, setQtyMap] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // ── Edit mode: `id` is the RETURN id — load it to get its estimate + current lines. ──
  const returnQuery = useQuery({
    queryKey: ['return', id],
    queryFn: () => apiFetch<EditReturnResponse>(`/api/returns/${id}`),
    enabled: isEdit && !!id,
    retry: false,
  });
  const editedReturn = returnQuery.data?.data.return;
  // In create mode `id` is the estimate id; in edit mode it's the return id.
  const estimateId = isEdit ? editedReturn?.estimate_id : Number(id);

  // ── Fetch returnable lines (exclude this return from the tally when editing). ──
  const {
    data,
    isLoading: returnableLoading,
    error,
  } = useQuery({
    queryKey: ['returnable', estimateId, isEdit ? id : 'new'],
    queryFn: () =>
      apiFetch<ReturnableResponse>(
        `/api/returns/returnable/${estimateId}${isEdit ? `?exclude_return=${id}` : ''}`,
      ),
    enabled: !!estimateId,
    retry: false, // 409 errors should not be retried
  });
  const isLoading = returnableLoading || (isEdit && returnQuery.isLoading);

  // ── Edit guard: admins only, OPEN returns only (matches the server). ──
  useEffect(() => {
    if (!isEdit || !editedReturn) return;
    const editable = user?.role === 'admin' && editedReturn.status === 'open';
    if (!editable) {
      toast.error('This return can no longer be edited.');
      navigate(`/returns/${id}`, { replace: true });
    }
  }, [isEdit, editedReturn, user, id, navigate]);

  // ── Prefill (edit mode) exactly once: the return's current qtys + date + notes. ──
  const prefilled = useRef(false);
  useEffect(() => {
    if (!isEdit || prefilled.current || !editedReturn || !data || !returnQuery.data) return;
    prefilled.current = true;
    const map: Record<number, string> = {};
    for (const it of returnQuery.data.data.items) {
      if (it.estimate_item_id != null) map[it.estimate_item_id] = String(it.qty);
    }
    setQtyMap(map);
    setDate(editedReturn.date);
    setNotes(editedReturn.notes ?? '');
  }, [isEdit, editedReturn, data, returnQuery.data]);

  // ── Derived state ──
  const lines = data?.data.lines ?? [];
  const estimate = data?.data.estimate;
  const backTo = isEdit ? `/returns/${id}` : `/estimates/${id}`;
  const backLabel = isEdit ? 'Back to Return' : 'Back to Estimate';

  const allFullyReturned = lines.length > 0 && lines.every((l) => l.returnable_qty === 0);

  const lineValues = useMemo(() => {
    const map: Record<number, number> = {};
    for (const line of lines) {
      const qty = parseFloat(qtyMap[line.estimate_item_id] ?? '0') || 0;
      map[line.estimate_item_id] = Math.round(qty * line.effective_unit_price);
    }
    return map;
  }, [lines, qtyMap]);

  const returnTotal = useMemo(
    () => Object.values(lineValues).reduce((acc, v) => acc + v, 0),
    [lineValues],
  );

  // ── Qty change handler ──
  function handleQtyChange(estimateItemId: number, value: string) {
    setQtyMap((prev) => ({ ...prev, [estimateItemId]: value }));
  }

  // ── Keyboard focus chain across the qty inputs ──
  // Each qty input registers its element by line id; Tab/Enter on one advances to the
  // next line's qty input (or the submit button on the last line), so the whole return
  // can be filled in without touching the mouse. Shift+Tab keeps normal back-navigation.
  const qtyRefs = useRef<Map<number, HTMLInputElement | null>>(new Map());
  const submitRef = useRef<HTMLButtonElement>(null);

  function registerQtyRef(estimateItemId: number, el: HTMLInputElement | null) {
    if (el) qtyRefs.current.set(estimateItemId, el);
    else qtyRefs.current.delete(estimateItemId);
  }

  function handleQtyKeyDown(e: React.KeyboardEvent<HTMLInputElement>, index: number) {
    // Only intercept forward navigation (Tab without shift, or Enter). Let everything
    // else — including Shift+Tab — fall through to the browser's default handling.
    if (e.key !== 'Enter' && !(e.key === 'Tab' && !e.shiftKey)) return;
    e.preventDefault();
    // Walk forward from the next line to the first input that's still focusable
    // (skips fully-returned/disabled rows). Land on the submit button past the end.
    for (let i = index + 1; i < lines.length; i++) {
      const next = qtyRefs.current.get(lines[i].estimate_item_id);
      if (next && !next.disabled) {
        next.focus();
        next.select();
        return;
      }
    }
    submitRef.current?.focus();
  }

  // ── Submit ──
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Validate: at least one line with qty > 0
    const activeLines = lines.filter((l) => {
      const qty = parseFloat(qtyMap[l.estimate_item_id] ?? '0') || 0;
      return qty > 0;
    });

    if (activeLines.length === 0) {
      toast.error('Enter a return quantity for at least one line.');
      return;
    }

    // Validate: each qty <= returnable_qty
    for (const line of activeLines) {
      const qty = parseFloat(qtyMap[line.estimate_item_id] ?? '0') || 0;
      if (qty > line.returnable_qty) {
        toast.error(
          `"${line.description}": return qty (${qty}) exceeds returnable qty (${line.returnable_qty}).`,
        );
        return;
      }
    }

    setSubmitting(true);
    const items = activeLines.map((l) => ({
      estimate_item_id: l.estimate_item_id,
      qty: parseFloat(qtyMap[l.estimate_item_id] ?? '0') || 0,
    }));
    try {
      if (isEdit) {
        // Edit in place — the server reverses old stock, recomputes, and keeps the SR number/status.
        await apiFetch(`/api/returns/${id}`, {
          method: 'PATCH',
          body: JSON.stringify({ date, notes: notes.trim() || undefined, items }),
        });
        toast.success('Return updated');
        queryClient.invalidateQueries({ queryKey: ['return', id] });
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        if (estimateId) {
          queryClient.invalidateQueries({ queryKey: ['estimate', String(estimateId)] });
        }
        navigate(`/returns/${id}`);
      } else {
        const res = await apiFetch<CreateReturnResponse>('/api/returns', {
          method: 'POST',
          body: JSON.stringify({
            estimate_id: Number(id),
            date,
            notes: notes.trim() || undefined,
            items,
          }),
        });
        toast.success(`Return ${res.data.return.number} created`);
        queryClient.invalidateQueries({ queryKey: ['estimate', id] });
        queryClient.invalidateQueries({ queryKey: ['returns'] });
        navigate(`/returns/${res.data.return.id}`);
      }
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : isEdit
            ? 'Failed to update return'
            : 'Failed to create return',
      );
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  // ── Error (including 409 not-returnable) ──
  if (error || !data) {
    const msg = error instanceof Error ? error.message : 'Could not load returnable lines.';
    return (
      <div className="p-4 md:p-6 max-w-4xl space-y-4">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>
        <div className="flex items-start gap-3 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <p className="text-sm text-destructive">{msg}</p>
        </div>
      </div>
    );
  }

  // ── Fully returned ──
  if (allFullyReturned) {
    return (
      <div className="p-4 md:p-6 max-w-4xl space-y-4">
        <Link
          to={backTo}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {backLabel}
        </Link>
        <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            This estimate is fully returned. There are no returnable quantities remaining.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl space-y-6">
      {/* ── Back nav ── */}
      <Link
        to={`/estimates/${id}`}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Estimate
      </Link>

      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {isEdit ? `Edit Return ${editedReturn?.number ?? ''}` : 'Create Return'}
        </h1>
        {estimate && (
          <p className="text-sm text-muted-foreground mt-1">
            Against <span className="font-medium text-foreground font-mono">{estimate.number}</span>{' '}
            for <span className="font-medium text-foreground">{estimate.customer_name}</span>
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ── Returnable lines table ── */}
        <Card>
          <div className="px-4 py-3 border-b bg-muted/30">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Returnable Lines
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/20">
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                    Description
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-20">
                    Sold
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                    Already Returned
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-24">
                    Returnable
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                    Return Qty
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                    Unit Price
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                    Line Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, lineIndex) => {
                  const isFullyReturned = line.returnable_qty === 0;
                  const qty = parseFloat(qtyMap[line.estimate_item_id] ?? '0') || 0;
                  const lineValue = lineValues[line.estimate_item_id] ?? 0;
                  const hasValue = lineValue > 0;

                  return (
                    <tr
                      key={line.estimate_item_id}
                      className={`border-b last:border-0 transition-colors ${
                        isFullyReturned
                          ? 'opacity-40 bg-muted/10'
                          : hasValue
                            ? 'bg-blue-50/40 dark:bg-blue-950/10'
                            : 'hover:bg-muted/20'
                      }`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-sm">{line.description}</div>
                        {line.category && (
                          <div className="text-xs text-muted-foreground">{line.category}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                        {line.sold_qty}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm text-muted-foreground">
                        {line.already_returned_qty > 0 ? (
                          <span className="text-amber-700 dark:text-amber-400">
                            {line.already_returned_qty}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                        {line.returnable_qty}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Input
                          ref={(el) => registerQtyRef(line.estimate_item_id, el)}
                          type="number"
                          min={0}
                          max={line.returnable_qty}
                          step={0.01}
                          value={qtyMap[line.estimate_item_id] ?? '0'}
                          onChange={(e) => handleQtyChange(line.estimate_item_id, e.target.value)}
                          onKeyDown={(e) => handleQtyKeyDown(e, lineIndex)}
                          disabled={isFullyReturned || submitting}
                          className="h-8 w-24 text-right text-xs tabular-nums ml-auto disabled:opacity-50"
                        />
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm text-muted-foreground">
                        {formatCents(line.effective_unit_price)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                        {qty > 0 ? (
                          <span className="text-blue-700 dark:text-blue-400">
                            {formatCents(lineValue)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Return total preview */}
          <div className="px-4 py-3 border-t bg-muted/10">
            <div className="ml-auto max-w-xs">
              <div className="flex items-center justify-between pt-1 font-semibold">
                <span>Return Total</span>
                <span
                  className={`tabular-nums text-base transition-colors ${
                    returnTotal > 0 ? 'text-blue-700 dark:text-blue-400' : 'text-muted-foreground'
                  }`}
                >
                  {formatCents(returnTotal)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Preview only — server is authoritative
              </p>
            </div>
          </div>
        </Card>

        {/* ── Date & Notes ── */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Return Details
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="return-date">
                  Return Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="return-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={submitting}
                  required
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="return-notes">Notes (optional)</Label>
              <textarea
                id="return-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
                rows={3}
                placeholder="Reason for return, condition of goods, etc."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* ── Actions ── */}
        <div className="flex items-center justify-between gap-4 pb-6">
          <Link
            to={backTo}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </Link>
          <Button ref={submitRef} type="submit" disabled={submitting || returnTotal === 0}>
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            {isEdit
              ? submitting
                ? 'Saving…'
                : 'Save Changes'
              : submitting
                ? 'Creating…'
                : 'Create Return'}
          </Button>
        </div>
      </form>
    </div>
  );
}
